import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { compose, pure, withHandlers } from 'recompose';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';

import * as actions from 'actions';
import {
  getSchedulesCollectionByGroup,
  isAtLeastOneAvailableToDeleteByGroup,
  getSchedulesFromExternal,
  requestInProcess,
  getSchedulesFromExternalByGroup,
  getSchedulesCollection,
} from 'selectors';

import Loader from './components/Loader';
import {
  useTableKeyboardNavigation,
  useScheduleValidation,
  useScheduleTableModals,
  useScheduleTableData,
  useScheduleTableActions,
  useScheduleTableExternalData,
} from './hooks';

import {
  StyledTable,
  TableWrapper,
  TableContainer,
  StyledTbody,
  StyledTd,
  StyledTr,
  StyledThead,
  StyledTh,
  RescheduleTableContainer,
  ActionBarWrapper,
  PropertiesTitle
} from './styles';

import ActionBar from './components/ActionBar';
import ScheduleModal from './components/SсheduleModal';
import useColumns from './components/Columns/columns';
import { useScheduleTable } from '../../../Application/SchedulePage/context';
import transformContactsToBorrower, {
  GROUP_ENUM,
  requiredFields,
  GROUP_COLUMN_CONFIG,
} from './utils';
import NoPropertiesModal from './components/NoPropertiesModal';
import ConfirmationModal from './components/ConfirmationModal';
import ScheduleTooltip from '../ScheduleTooltip';

const propTypes = {
  externalSchedules: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  collection: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  allSchedulesCollection: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  containerWidth: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
  group: PropTypes.string.isRequired,
  handleAdd: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleAddressChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  title: PropTypes.string,
  searchClicked: PropTypes.bool,
  fetchSchedules: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      token: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  targetId: PropTypes.string,
  targetType: PropTypes.string,
  batchUpdateSchedules: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isImportLoading: PropTypes.bool,
  fetchSchedulesFromExternalDirectly: PropTypes.func.isRequired,
};

const defaultProps = {
  externalSchedules: [],
  collection: [],
  allSchedulesCollection: [],
  title: undefined,
  searchClicked: false,
  targetId: null,
  targetType: null,
  isLoading: false,
  isImportLoading: false,
};

function ScheduleTable({
  containerWidth,
  group,
  handleChange,
  onDelete,
  fetchSchedules,
  collection,
  allSchedulesCollection,
  match,
  targetId,
  targetType,
  batchUpdateSchedules,
  isLoading,
  isImportLoading,
  fetchSchedulesFromExternalDirectly,
}) {
  const [selectedRows, setSelectedRows] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [, setEditingRows] = useState(new Set());

  const tableDOMRef = useRef(null);

  const scheduleData = useSelector((state) => (
    getSchedulesFromExternalByGroup(state, group)
  ));

  const {
    fetchSchedulesFromExternal,
    contacts,
    searchButtonClicked,
    setSearchButtonClicked,
  } = useScheduleTable();

  const {
    modalWindowVisible,
    setModalWindowVisible,
    noPropertiesModalVisible,
    setNoPropertiesModalVisible,
    confirmationModalVisible,
    setConfirmationModalVisible,
    handleCloseModal,
    handleCloseNoPropertiesModal,
    handleCancelNotMine,
  } = useScheduleTableModals();

  const { processedData, allRowIndices, enhancedHandleChange } = useScheduleTableData(
    collection,
    handleChange,
    onDelete,
    group,
    setValidationErrors,
    setEditingRows,
  );

  const validation = useScheduleValidation(
    processedData,
    validationErrors,
    requiredFields,
  );

  const {
    handleMoveToRental,
    handleMoveToBridge,
    handleMoveToPrimary,
    handleMoveToBridgeRent,
    handleMoveToNewConstruction,
    handleNotMine,
    handleConfirmNotMine,
  } = useScheduleTableActions(
    selectedRows,
    setSelectedRows,
    processedData,
    batchUpdateSchedules,
    match,
    targetId,
    targetType,
    fetchSchedules,
    onDelete,
    setConfirmationModalVisible,
  );

  const { onAppend, onReplace, onCancel } = useScheduleTableExternalData(
    fetchSchedulesFromExternalDirectly,
    match,
    contacts,
    setSearchButtonClicked,
    setNoPropertiesModalVisible,
    setModalWindowVisible,
    fetchSchedules,
  );

  const handleValidationChange = useCallback((fieldKey, error, hasError) => {
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      if (hasError && error) {
        newErrors[fieldKey] = error;
      } else {
        delete newErrors[fieldKey];
      }

      return newErrors;
    });
  }, []);


  const isAllSelected = selectedRows.length === allRowIndices.length &&
    allRowIndices.length > 0;

  const data = processedData;
  const navigation = useTableKeyboardNavigation(data, GROUP_COLUMN_CONFIG[group], tableDOMRef);

  const columns = useMemo(() => useColumns({
    group,
    scheduleData,
    handleChange: enhancedHandleChange,
    setSelectedRows,
    allRowIndices,
    onValidationChange: handleValidationChange,
    selectedRows,
    isAllSelected,
    isFieldMissing: validation.isFieldMissing,
    isDuplicateAddress: validation.isDuplicateAddress,
    navigation,
  }), [
    scheduleData,
    allRowIndices,
    isAllSelected,
    handleValidationChange,
    selectedRows,
    group,
    enhancedHandleChange,
    validation,
    navigation,
  ]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
  });

  useEffect(() => {
    fetchSchedules(match.params.token);

    if (searchButtonClicked) {
      const { isValid } = validation.validateAllSchedulesCollection();

      if (!isValid) {
        setModalWindowVisible(true);
      } else {
        fetchSchedulesFromExternalDirectly(
          match.params.token,
          transformContactsToBorrower(contacts),
        )
          .then((response) => {
            if (response.collection.properties.length === 0) {
              setNoPropertiesModalVisible(true);
            }

            fetchSchedules(match.params.token);
          });
        setSearchButtonClicked(false);
      }
    }
  }, [
    searchButtonClicked,
    table,
    fetchSchedules,
    match.params.token,
    setSearchButtonClicked,
    setModalWindowVisible
  ]);

  return (
    <>
      {modalWindowVisible && (
        <ScheduleModal
          onAppend={onAppend}
          onReplace={onReplace}
          onCancel={onCancel}
          onClose={handleCloseModal}
        />
      )}

      {confirmationModalVisible && (
        <ConfirmationModal
          onProceed={handleConfirmNotMine}
          onCancel={handleCancelNotMine}
        />
      )}

      {noPropertiesModalVisible && (
        <NoPropertiesModal
          onClose={handleCloseNoPropertiesModal}
        />
      )}

      <RescheduleTableContainer>
        <ActionBarWrapper>
          <PropertiesTitle> Properties List <ScheduleTooltip width={1.4} text="Enter the data manually or upload it using the options above." /></PropertiesTitle>
          <ActionBar
            onMoveToNewConstruction={handleMoveToNewConstruction}
            onMoveToRental={handleMoveToRental}
            onMoveToBridge={handleMoveToBridge}
            onMoveToPrimary={handleMoveToPrimary}
            onMoveToBridgeRent={handleMoveToBridgeRent}
            onNotMine={handleNotMine}
            group={group}
            disabled={isLoading || selectedRows.length === 0}
          />
        </ActionBarWrapper>

        <TableContainer>
          {(isLoading || isImportLoading) && <Loader />}
          <TableWrapper
            tabIndex={0}
            style={{ width: containerWidth }}
            ref={tableDOMRef}
          >
            <StyledTable>
              <StyledThead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <StyledTr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <StyledTh
                        key={header.id}
                        style={{
                          width: header.getSize(),
                          position: 'relative',
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {header.column.getCanResize() && (
                          <div
                            {...{
                              onMouseDown: header.getResizeHandler(),
                              onTouchStart: header.getResizeHandler(),
                              className: `resizer ${header.column.getIsResizing() ? 'isResizing' : ''}`,
                              style: {
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                height: '100%',
                                width: '5px',
                                background: 'rgba(0, 0, 0, 0.5)',
                                cursor: 'col-resize',
                                userSelect: 'none',
                                touchAction: 'none',
                                opacity: header.column.getIsResizing() ? 1 : 0,
                              },
                            }}
                          />
                        )}
                      </StyledTh>
                    ))}
                  </StyledTr>
                ))}
              </StyledThead>

              <StyledTbody>
                {table.getRowModel().rows.map((row) => (
                  <StyledTr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <StyledTd
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </StyledTd>
                    ))}
                  </StyledTr>
                ))}
              </StyledTbody>
            </StyledTable>
          </TableWrapper>
        </TableContainer>
      </RescheduleTableContainer>
    </>
  );
}

ScheduleTable.propTypes = propTypes;
ScheduleTable.defaultProps = defaultProps;

function mapStateToProps(state, props) {
  return {
    externalSchedules: getSchedulesFromExternal(state),
    collection: getSchedulesCollectionByGroup(state, props.group),
    allSchedulesCollection: getSchedulesCollection(state),
    isAtLeastOneAvailableToDeleteByGroupCollection: isAtLeastOneAvailableToDeleteByGroup(
      state,
      props,
    ),
    isLoading: requestInProcess(state, 'SCHEDULES'),
    isImportLoading: requestInProcess(state, 'SCHEDULES_IMPORT'),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    fetchSchedules: bindActionCreators(actions.fetchSchedules, dispatch),
    fetchSchedulesFromExternalDirectly: bindActionCreators(
      actions.fetchSchedulesFromExternalDirectly,
      dispatch,
    ),
    batchUpdateSchedules: bindActionCreators(actions.batchUpdateSchedules, dispatch),
    removeApplicationScheduleRequest: bindActionCreators(
      actions.removeApplicationScheduleRequest,
      dispatch,
    ),
    addApplicationScheduleRequest: bindActionCreators(
      actions.addApplicationScheduleRequest,
      dispatch,
    ),
    editApplicationScheduleRequest: bindActionCreators(
      actions.editApplicationScheduleRequest,
      dispatch,
    ),
  };
}

const enhance = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withHandlers({
    onDelete: ({ target, removeApplicationScheduleRequest, match }) => (record) => {
      const { token } = match.params;
      removeApplicationScheduleRequest(target, token, record);
    },

    handleChange: ({
      target,
      editApplicationScheduleRequest,
      targetType,
      targetId,
      match,
      group,
      addApplicationScheduleRequest,
    }) => (value, column, record) => {
      const { token } = match.params;
      const property = record;

      if (record._new || record.new) {
        addApplicationScheduleRequest(target, property);
      }

      if (!record.id) {
        property[column] = value;
        property.group = GROUP_ENUM[group];
      }

      editApplicationScheduleRequest(
        target,
        property,
        column,
        value,
        targetId,
        targetType,
        token,
      );
    },
  }),
  pure,
);

export default enhance(ScheduleTable);
